/*
 * BrimarTrack — CCW storefront event wiring.
 *
 * The tracker itself (loaded by snippets/brimar-track.liquid) already captures
 * page views, SPA navigation, scroll depth, engaged time, clicks — CTA,
 * mailto, tel, outbound, file download — return visits and form submits, with
 * no wiring at all. This file adds only what the tracker cannot work out from
 * the DOM: who the shopper is, and the commerce events whose names the CRM's
 * lead-scoring rules match on.
 *
 * Not a custom element, deliberately. Custom elements are this theme's
 * convention (24 definitions in scripts.js alone), but they upgrade a host
 * element and this integration has no DOM of its own. A guarded IIFE is the
 * honest shape, matching welcome-signup.js.
 *
 * Event names are NOT free-form. The CRM accepts almost anything by regex, but
 * only these are matched by its rollups and its lead-scoring rules
 * (crm/services/scoring.py): cart_add, checkout_started, quote_started,
 * product_viewed, order_completed. Inventing a name silently scores nothing.
 *
 * checkout_started and order_completed are NOT here. They happen on Shopify's
 * checkout, which no theme code can reach — they live in the custom pixel at
 * src/custom-pixels/brimar-track.js. Nothing in this file should try to infer
 * them from a click on a checkout button: the shopper may never arrive.
 *
 * Navigation is not a hazard here. The tracker batches on a 1.5 s timer but
 * flushes with sendBeacon on pagehide and on visibilitychange→hidden
 * (track.js:240-242), so an event emitted immediately before a redirect — the
 * themeSettings.redirectToCart path in product-form.js sets location.href
 * before it publishes — still leaves the browser.
 */

(function () {
  "use strict";

  if (window.__brimarTrackInstalled) {
    return;
  }
  window.__brimarTrackInstalled = true;

  var settings = window.brimarTrackSettings;

  if (!settings) {
    console.error(
      "[brimar-track] window.brimarTrackSettings is missing. Is snippets/brimar-track-config.liquid rendered before this script?",
    );
    return;
  }

  /*
   * No address means the kill switch is on and the loader never created the
   * queue, so every call below would be a silent no-op. Leave quietly rather
   * than logging — an intentionally disabled tracker is not an error.
   */
  if (!settings.apiBaseUrl) {
    return;
  }

  /*
   * Always via this helper, never by holding a reference to window.BrimarTrack.
   * Before track.js arrives that global is the loader's queue array; afterwards
   * it is replaced outright by the real API object (track.js:287). A reference
   * captured at load time would keep pointing at the discarded array and every
   * event through it would be lost.
   */
  function track(name, props) {
    if (window.BrimarTrack) {
      window.BrimarTrack.track(name, props);
    }
  }

  /* Shopify returns money in cents everywhere; the CRM expects major units. */
  function toAmount(cents) {
    return typeof cents === "number" ? cents / 100 : undefined;
  }

  // -------------------------------------------------------------------------
  // Identity
  // -------------------------------------------------------------------------

  /*
   * Shopify re-renders `customer` server-side on every page, so this runs on
   * every load while signed in rather than on a change event as brimarstore
   * does. identify() is idempotent, so the repetition costs nothing, and the
   * CRM backfills whatever this browser recorded before sign-in.
   */
  if (settings.customer && settings.customer.email) {
    if (window.BrimarTrack) {
      window.BrimarTrack.identify(settings.customer.email);
    }
  }

  /*
   * Reset on a deliberate sign-out only, so the next person on a shared browser
   * is recorded as someone new.
   *
   * Bound to the logout link rather than to the absence of a customer: an
   * anonymous visitor is the ordinary state of most page views, and resetting
   * there would drop the visitor cookie on every one of them and make each look
   * like a first-time visit. The theme renders exactly one logout route, in
   * sections/main-customers-account.liquid.
   *
   * Delegated from the document because that link only exists on the account
   * page, and capture-phase so it runs before any handler that might cancel the
   * event. The reset is synchronous, so it completes before the navigation.
   */
  document.addEventListener(
    "click",
    function (event) {
      var link = event.target.closest && event.target.closest('a[href*="/account/logout"]');
      if (link && window.BrimarTrack) {
        window.BrimarTrack.reset();
      }
    },
    true,
  );

  // -------------------------------------------------------------------------
  // Product views
  // -------------------------------------------------------------------------

  /*
   * The tracker's own page view already carries the landing SKU, read off the
   * <meta name="brimar:sku"> tag that brimar-track.liquid renders. What it
   * cannot see is the shopper changing variant, which on this theme never
   * navigates — so the meta tag goes stale and the SKU they actually considered
   * would never be reported.
   *
   * The DOM event is used in preference to the pubsub channel that fires
   * alongside it (scripts.js:1055 and :1062 publish both). pubsub.js exposes
   * subscribe() as a global from a separate deferred script; a DOM listener
   * depends on nothing having loaded first.
   *
   * Read live from the variant rather than from the theme's rendered SKU, which
   * sits behind two merchant toggles and can be switched off in the editor.
   */
  document.addEventListener("product:variant-change", function (event) {
    var variant = event.detail && event.detail.variant;

    if (!variant) {
      return;
    }

    track("product_viewed", {
      sku: variant.sku,
      value: toAmount(variant.price),
      currency: "USD",
    });
  });

  // -------------------------------------------------------------------------
  // Cart adds
  // -------------------------------------------------------------------------

  /*
   * Two code paths add to this cart and they report completely differently.
   *
   *   1. product-form.js — one product at a time, from the product page, a
   *      product card or the quick-view drawer. Dispatches `cart:item-add`
   *      carrying the added line itself.
   *
   *   2. Quantity changes — CartItems in scripts.js, which MiniCart extends, so
   *      this covers both the cart page and the drawer. Publishes `cart:update`
   *      with the WHOLE cart, never saying what changed.
   *
   * The bulk paths (BulkAdd in scripts.js, bulk-order-list.js) publish through
   * the same `cart:update` channel, but neither is currently reachable on this
   * store: BulkAdd renders only when settings.products_quickadd_no_variants is
   * 'quantity_picker' and it is 'add_to_cart', and sections/bulk-order.liquid is
   * referenced by no template. Checked 2026-10-08. They are covered here anyway
   * because enabling either is a theme-editor change away, and would otherwise
   * go unreported with nothing to indicate it.
   *
   * Hence the baseline below: line quantities as they stood, diffed against
   * each new cart. It is seeded server-side in brimar-track-config.liquid so
   * the first change on a page is attributable without this file having to
   * fetch /cart.js on every page load just to learn where the cart started.
   */
  var baseline = settings.cart || {};

  /*
   * Only increases are reported. cart_remove exists in the CRM's vocabulary but
   * no scoring rule matches it, so emitting it would buy nothing — and would
   * cost something, because a decrease is exactly what a stale baseline
   * fabricates. Reporting adds only means the worst a drifted baseline can do
   * is stay quiet.
   *
   * `merge` says whether `items` is the whole cart or only part of it.
   *
   * cart:update carries every line, so the baseline is REPLACED — lines absent
   * from the payload are gone from the cart and must drop out, or removing a
   * line would leave a stale entry that suppresses a later re-add.
   *
   * cart:item-add carries only the line just added, so the baseline is MERGED.
   * Replacing it there would forget every other line in the cart, and the next
   * cart:update would then see all of them as increases and report the entire
   * cart as freshly added.
   */
  function reportIncreases(items, options) {
    var merge = Boolean(options && options.merge);
    var next = merge ? baseline : {};

    items.forEach(function (item) {
      /* Before the assignment below, which overwrites what we are comparing to. */
      var delta = item.quantity - (baseline[item.key] || 0);

      next[item.key] = item.quantity;

      if (delta > 0) {
        track("cart_add", {
          sku: item.sku,
          qty: delta,
          value: toAmount(item.price * delta),
          currency: "USD",
        });
      }
    });

    baseline = next;
  }

  /*
   * `quantity` on the line returned by /cart/add.js is the line's NEW TOTAL,
   * not the amount just added. Verified against the live store on 2026-10-08:
   * adding one unit to a line that already held one reported quantity 2.
   *
   * So the add response is treated exactly like a cart snapshot — one line of
   * it — and runs through the same diff as cart:update. The delta is what gets
   * reported, and the total becomes the new baseline.
   *
   * Reading it as the amount added, which is the intuitive guess and what this
   * file did first, is wrong twice over: a repeat add reports the whole line
   * instead of the increase, and the baseline then runs ahead of the real cart
   * and silently swallows the next increase. That is exactly what live testing
   * caught — clicking + in the mini-cart fired nothing.
   */
  document.addEventListener("cart:item-add", function (event) {
    var items = (event.detail && event.detail.items) || [];

    reportIncreases(items, { merge: true });
  });

  /*
   * The bulk paths publish through pubsub only — there is no DOM event to use
   * instead, so this is the one place that depends on pubsub.js having loaded.
   * It is rendered before this file in head-scripts.liquid and both are
   * deferred, so they run in document order; the guard is for the case where
   * that order is ever changed.
   */
  try {
    /*
     * The try/catch is not decoration. PUB_SUB_EVENTS is a top-level `const`,
     * so if this file were ever moved ABOVE pubsub.js the binding would exist
     * but sit in its temporal dead zone, and even `typeof` on it throws — the
     * one case this guard is here to report would instead take the whole file
     * down, taking identity and product tracking with it.
     */
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (payload) {
        var items = payload && payload.cartData && payload.cartData.items;

        if (items) {
          reportIncreases(items);
        }
      });
    } else {
      throw new Error("pubsub.js has not loaded");
    }
  } catch (error) {
    console.error(
      "[brimar-track] cannot subscribe to cart:update, so bulk cart adds will not be tracked. Is brimar-track rendered after pubsub.js in snippets/head-scripts.liquid?",
      error,
    );
  }

  // -------------------------------------------------------------------------
  // Quote requests
  // -------------------------------------------------------------------------

  /*
   * Request a Quote is a HubSpot-rendered embed, so the tracker's automatic
   * form capture never sees it — the fields live in HubSpot's own iframe, out
   * of reach of a submit listener on this document.
   *
   * Hooked to HubSpot's own window event rather than to the
   * `hubspot-form-success` that assets/product.quote-form.js:146 republishes
   * through Shopify. `Shopify.analytics.publish` is the documented way to send
   * a custom event TO a web pixel; there is no matching storefront-side
   * `subscribe`, so listening for it there is a guard that never opens and an
   * event that never fires. This listens to the source instead — the same
   * window event product.quote-form.js itself listens for, two lines above
   * where it republishes.
   *
   * Unambiguous on this store: product.quote-form.js is the only embedded
   * HubSpot form in the theme, so an `hs-form-event` here can only be a quote.
   * The footer and welcome signups also reach HubSpot but post to its Forms
   * API directly and dispatch nothing. If a second HubSpot embed is ever
   * added, this needs narrowing by detail.formId.
   *
   * Named `quote_submitted`, not `quote_started`, even though only the latter
   * currently carries a lead-scoring rule (20 points, the highest web signal
   * the CRM has). This event fires on SUCCESS — the quote was sent, not begun —
   * and claiming otherwise to collect points would put a false story in the
   * CRM about what the shopper actually did. `quote_submitted` is already in
   * the CRM's CONVERSIONS list, so it is counted as a conversion; it just has
   * no scoring rule yet. Adding one is a CRM-side change and should be worth
   * MORE than quote_started, not less.
   */
  window.addEventListener("hs-form-event:on-submission:success", function (event) {
    track("quote_submitted", {
      form_id: event.detail && event.detail.formId,
    });
  });
})();
